import { useContext, useEffect, useState } from "react";
import { Form, Input, Modal, Select, Space, message } from "antd";
import { EditOutlined, ShopOutlined } from "@ant-design/icons";
import { ISocialInstitution } from "../../../../interfaces";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import { formatCnpj } from "../../../../utils/Masks";
import { isValidCNPJ } from "../../../../utils/Functions";
import { EmitSuccessAlert } from "../../../../utils/EmitSuccessAlert";
import { EmitError } from "../../../../utils/EmitError";
import SocialInstitutionService from "../../../../services/http/services/SocialInstitutionService";
import useAuth from "../../../../hooks/useAuth";

interface IProps {
    open: boolean;
    onClose: () => void;
    socialInstitution?: ISocialInstitution | null;
    onSuccess: () => void;
}

export default function ModalSocialInstitutionForm({
    open,
    onClose,
    socialInstitution,
    onSuccess,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const { activeCity } = useAuth();
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditing = Boolean(socialInstitution?.id);

    useEffect(() => {
        if (open) {
            if (socialInstitution) {
                form.setFieldsValue({
                    name: socialInstitution.name || "",
                    cnpj: socialInstitution.cnpj ? formatCnpj(socialInstitution.cnpj) : "",
                    active: socialInstitution.active === undefined ? true : socialInstitution.active,
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    active: true,
                });
            }
        }
    }, [open, socialInstitution, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!activeCity || !activeCity.id) {
                message.error("Selecione um município ativo!");
                return;
            }

            const cleanCnpj = values.cnpj.replace(/\D/g, "");
            if (!isValidCNPJ(cleanCnpj) || cleanCnpj.length !== 14) {
                form.setFields([
                    {
                        name: "cnpj",
                        errors: ["CNPJ informado é inválido!"],
                    },
                ]);
                return;
            }

            setIsSubmitting(true);

            if (isEditing && socialInstitution) {
                await SocialInstitutionService.update({
                    id: socialInstitution.id,
                    data: {
                        ...socialInstitution,
                        name: values.name.trim(),
                        cnpj: cleanCnpj,
                        active: values.active,
                    },
                });
                EmitSuccessAlert("Instituição social atualizada com sucesso!");
            } else {
                await SocialInstitutionService.create({
                    countyId: activeCity.id,
                    name: values.name.trim(),
                    cnpj: cleanCnpj,
                    active: values.active,
                });
                EmitSuccessAlert("Instituição social cadastrada com sucesso!");
            }

            setIsSubmitting(false);
            onSuccess();
            onClose();
        } catch (err: any) {
            setIsSubmitting(false);
            if (err?.errorFields) {
                // Form validation errors
                return;
            }
            EmitError(err, true, "modal_social_institution_submit");
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            okText={isEditing ? "Salvar Alterações" : "Cadastrar Instituição"}
            cancelText="Cancelar"
            confirmLoading={isSubmitting}
            centered
            destroyOnClose
            width={560}
            title={
                <Space align="center" size={10}>
                    <div
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            background: isEditing
                                ? isDark ? "rgba(250, 173, 20, 0.15)" : "#fffbe6"
                                : isDark ? "rgba(22, 119, 255, 0.15)" : "#e6f4ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {isEditing ? (
                            <EditOutlined style={{ color: "#faad14", fontSize: 18 }} />
                        ) : (
                            <ShopOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                        )}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                            {isEditing ? "Editar Instituição Social" : "Nova Instituição Social"}
                        </div>
                        <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 2 }}>
                            {isEditing
                                ? "Atualize as informações cadastrais da entidade"
                                : "Cadastre uma nova entidade beneficente no sistema"}
                        </div>
                    </div>
                </Space>
            }
            bodyStyle={{ padding: "20px 0 8px 0" }}
        >
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                initialValues={{ active: true }}
            >
                <Form.Item
                    name="name"
                    label="Nome da Instituição"
                    rules={[
                        { required: true, message: "Por favor, digite o nome da instituição social!" },
                        { min: 3, message: "O nome deve ter ao menos 3 caracteres!" },
                    ]}
                >
                    <Input
                        placeholder="Ex: Associação de Pais e Amigos (APAE)"
                        style={{ borderRadius: 8, height: 40 }}
                    />
                </Form.Item>

                <Form.Item
                    name="cnpj"
                    label="CNPJ"
                    rules={[
                        { required: true, message: "Por favor, informe o CNPJ da instituição!" },
                    ]}
                >
                    <Input
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        style={{ borderRadius: 8, height: 40, fontFamily: "monospace" }}
                        onChange={(e) => {
                            form.setFieldsValue({
                                cnpj: formatCnpj(e.target.value),
                            });
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="active"
                    label="Status"
                    rules={[{ required: true, message: "Selecione o status!" }]}
                >
                    <Select
                        style={{ height: 40 }}
                        options={[
                            { label: "Ativo", value: true },
                            { label: "Inativo", value: false },
                        ]}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}
